/*
================================================================================
FILE HEADER — apps/studio-web/src/components/FunctionsPage.tsx
================================================================================

CHANGE LOG
----------
| #  | Date       | Who               | Why                      | What Changed                |
|----|------------|-------------------|--------------------------|-----------------------------|
| 1  | 2026-07-02 | Claude Sonnet 4.6 | Functions tab (Phase 3)  | Created — full Mimiq-parity |
|    |            |                   |                          | Function Library page        |

FILES THAT IMPORT THIS FILE
-----------------------------
- apps/studio-web/src/App.tsx

EXPORTS COVERED
---------------
- FunctionsPage — full Functions Library: folder tree + detail panel

CALLER → CALLEE MAP
--------------------
- FunctionsPage → db.functions (useLiveQuery, CRUD)
- FunctionsPage → db.functionFolders (useLiveQuery, CRUD)
- FunctionsPage → onEditInRecorder (prop) → App.tsx loads steps into recorder
- FunctionsPage → onSaveAsFunction (receives steps from recorder via App.tsx)

IMPORTANT POINTS
----------------
1. DBFunction.irJson stores IRStep[] (not a full TestIR). Parse with JSON.parse()
   to get IRStep[] for display and editing.
2. Folder tree is built from a flat list via buildFolderTree(). Root-level
   functions (folderId === undefined) are shown above the folder list.
3. "+ Add" parameter form uses inline state — not a modal — matching Mimiq UX.
4. "Edit Steps in Recorder" calls onEditInRecorder(fn) which switches to
   the record tab in App.tsx with the function's steps loaded.
5. Step display shows type badge + label (or first candidate value as fallback).

ISSUES FACED / KNOWN GOTCHAS
-----------------------------
- useLiveQuery returns undefined on first render (Dexie loading state). Always
  guard with ?? [] before mapping.
================================================================================
*/

import { useState, useMemo } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import { db } from '../lib/db.js';
import type { DBFunction, DBFunctionFolder, DBFunctionParam } from '../lib/db.js';
import { newId } from '@plex/ir';
import type { IRStep } from '@plex/ir';

// ─── Types ────────────────────────────────────────────────────────────────────

interface FolderNode {
  folder: DBFunctionFolder;
  children: FolderNode[];
  fns: DBFunction[];
}

// ─── Tree builder ─────────────────────────────────────────────────────────────

function buildFolderTree(
  folders: DBFunctionFolder[],
  fns: DBFunction[],
  parentId?: string,
): FolderNode[] {
  return folders
    .filter(f => f.parentId === parentId)
    .sort((a, b) => a.name.localeCompare(b.name))
    .map(folder => ({
      folder,
      children: buildFolderTree(folders, fns, folder.id),
      fns: fns.filter(fn => fn.folderId === folder.id).sort((a, b) => a.name.localeCompare(b.name)),
    }));
}

// ─── Step label helper ────────────────────────────────────────────────────────

function stepDisplayLabel(step: IRStep): string {
  if ('label' in step && step.label) return step.label as string;
  // Try common target fields
  const s = step as Record<string, unknown>;
  if (s.selector && typeof s.selector === 'object') {
    const sel = s.selector as { description?: string; candidates?: Array<{ value: string }> };
    if (sel.description) return sel.description;
    if (sel.candidates?.[0]?.value) return sel.candidates[0].value;
  }
  if (typeof s.url === 'string') return s.url;
  if (typeof s.text === 'string') return `"${s.text}"`;
  if (typeof s.key === 'string') return s.key;
  if (typeof s.value === 'string') return s.value;
  return '';
}

// ─── Styles ───────────────────────────────────────────────────────────────────

const PAGE: React.CSSProperties = {
  display: 'flex', height: '100%', overflow: 'hidden',
  background: '#09090b', color: '#e4e4e7', fontFamily: 'inherit',
};

const SIDEBAR: React.CSSProperties = {
  width: 240, flexShrink: 0,
  borderRight: '1px solid #27272a',
  display: 'flex', flexDirection: 'column',
  background: '#0d0d10',
  overflow: 'hidden',
};

const DETAIL: React.CSSProperties = {
  flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden',
};

const BTN = (active = false, danger = false): React.CSSProperties => ({
  display: 'inline-flex', alignItems: 'center', gap: 5,
  padding: '5px 12px', borderRadius: 6, fontSize: 12, fontWeight: 500,
  cursor: 'pointer', border: '1px solid',
  background: active ? '#7c3aed' : 'transparent',
  borderColor: active ? '#7c3aed' : danger ? '#7f1d1d' : '#3f3f46',
  color: active ? '#fff' : danger ? '#f87171' : '#a1a1aa',
  transition: 'all 0.15s',
});

const INPUT: React.CSSProperties = {
  background: '#18181b', border: '1px solid #3f3f46',
  borderRadius: 6, padding: '5px 10px', color: '#e4e4e7',
  fontSize: 12, outline: 'none', width: '100%', boxSizing: 'border-box',
};

const BADGE: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center',
  padding: '2px 8px', borderRadius: 99, fontSize: 11,
  background: '#27272a', color: '#a1a1aa', fontWeight: 500,
};

const STEP_TYPE_BADGE: React.CSSProperties = {
  display: 'inline-block', padding: '2px 7px',
  borderRadius: 4, fontSize: 10, fontWeight: 600,
  fontFamily: 'monospace', background: '#1c1c1f', color: '#a78bfa',
  border: '1px solid #3f3f46', flexShrink: 0,
};

// ─── Sub-components ───────────────────────────────────────────────────────────

function SidebarSearch({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <div style={{ padding: '10px 12px', borderBottom: '1px solid #27272a' }}>
      <div style={{ position: 'relative' }}>
        <span style={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', color: '#52525b', fontSize: 12 }}>🔍</span>
        <input
          value={value}
          onChange={e => onChange(e.target.value)}
          placeholder="Search functions..."
          style={{ ...INPUT, paddingLeft: 26 }}
        />
      </div>
    </div>
  );
}

function FolderRow({
  node, depth, selectedFnId, expanded, onToggle, onSelectFn,
  onAddSubfolder, onDeleteFolder, onExportFolder,
}: {
  node: FolderNode;
  depth: number;
  selectedFnId: string | null;
  expanded: boolean;
  onToggle: () => void;
  onSelectFn: (fn: DBFunction) => void;
  onAddSubfolder: (parentId: string) => void;
  onDeleteFolder: (folder: DBFunctionFolder) => void;
  onExportFolder: (folder: DBFunctionFolder) => void;
}) {
  const [hover, setHover] = useState(false);
  const count = node.fns.length + node.children.reduce((acc, c) => acc + c.fns.length, 0);

  return (
    <div>
      {/* Folder row */}
      <div
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        style={{
          display: 'flex', alignItems: 'center', gap: 6,
          padding: `5px 10px 5px ${10 + depth * 14}px`,
          cursor: 'pointer', userSelect: 'none',
          background: hover ? 'rgba(255,255,255,0.04)' : 'transparent',
        }}
        onClick={onToggle}
      >
        <span style={{ fontSize: 10, color: '#52525b', width: 10, flexShrink: 0 }}>{expanded ? '▼' : '▶'}</span>
        <span style={{ fontSize: 13 }}>📁</span>
        <span style={{ flex: 1, fontSize: 12, color: '#d4d4d8', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {node.folder.name}
        </span>
        {hover ? (
          <div style={{ display: 'flex', gap: 2 }} onClick={e => e.stopPropagation()}>
            <button title="Add subfolder" onClick={() => onAddSubfolder(node.folder.id)}
              style={{ background: 'none', border: 'none', color: '#52525b', cursor: 'pointer', padding: '1px 4px', fontSize: 11, borderRadius: 3 }}>
              +📁
            </button>
            <button title="Export folder" onClick={() => onExportFolder(node.folder)}
              style={{ background: 'none', border: 'none', color: '#52525b', cursor: 'pointer', padding: '1px 4px', fontSize: 11, borderRadius: 3 }}>
              ↓
            </button>
            <button title="Delete folder" onClick={() => onDeleteFolder(node.folder)}
              style={{ background: 'none', border: 'none', color: '#52525b', cursor: 'pointer', padding: '1px 4px', fontSize: 11, borderRadius: 3 }}>
              ✕
            </button>
          </div>
        ) : (
          <span style={{ fontSize: 10, color: '#52525b' }}>{count}</span>
        )}
      </div>

      {/* Contents */}
      {expanded && (
        <div>
          {node.fns.map(fn => (
            <FnRow key={fn.id} fn={fn} depth={depth + 1} selected={selectedFnId === fn.id} onSelect={() => onSelectFn(fn)} />
          ))}
          {node.children.map(child => (
            <FolderRow
              key={child.folder.id} node={child} depth={depth + 1}
              selectedFnId={selectedFnId} expanded={false}
              onToggle={() => {}} onSelectFn={onSelectFn}
              onAddSubfolder={onAddSubfolder}
              onDeleteFolder={onDeleteFolder}
              onExportFolder={onExportFolder}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function FnRow({ fn, depth, selected, onSelect }: {
  fn: DBFunction; depth: number; selected: boolean; onSelect: () => void;
}) {
  const [hover, setHover] = useState(false);
  return (
    <div
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onClick={onSelect}
      style={{
        display: 'flex', alignItems: 'center', gap: 6,
        padding: `5px 10px 5px ${10 + depth * 14}px`,
        cursor: 'pointer', userSelect: 'none',
        background: selected ? 'rgba(124,58,237,0.2)' : hover ? 'rgba(255,255,255,0.04)' : 'transparent',
        borderLeft: selected ? '3px solid #7c3aed' : '3px solid transparent',
      }}
    >
      <span style={{ fontSize: 13, color: '#a78bfa' }}>⚙</span>
      <span style={{
        flex: 1, fontSize: 12,
        color: selected ? '#c4b5fd' : '#d4d4d8',
        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
      }}>
        {fn.name}
      </span>
      <span style={{ fontSize: 10, color: '#52525b', flexShrink: 0 }}>{fn.stepCount}s</span>
    </div>
  );
}

// ─── New Function Modal ───────────────────────────────────────────────────────

function NewFunctionModal({ folders, onConfirm, onClose }: {
  folders: DBFunctionFolder[];
  onConfirm: (name: string, folderId?: string) => void;
  onClose: () => void;
}) {
  const [name, setName] = useState('');
  const [folderId, setFolderId] = useState('');

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.7)' }}>
      <div style={{ background: '#18181b', border: '1px solid #3f3f46', borderRadius: 10, padding: 24, width: 360, display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div style={{ fontSize: 15, fontWeight: 600, color: '#e4e4e7' }}>New Function</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <label style={{ fontSize: 11, color: '#71717a' }}>Function name *</label>
          <input
            autoFocus value={name} onChange={e => setName(e.target.value)}
            placeholder="e.g. LoginFlow"
            style={INPUT}
            onKeyDown={e => { if (e.key === 'Enter' && name.trim()) onConfirm(name.trim(), folderId || undefined); }}
          />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <label style={{ fontSize: 11, color: '#71717a' }}>Folder (optional)</label>
          <select
            value={folderId}
            onChange={e => setFolderId(e.target.value)}
            style={{ ...INPUT, appearance: 'none' }}
          >
            <option value="">— No folder —</option>
            {folders.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
          </select>
        </div>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={BTN()}>Cancel</button>
          <button
            onClick={() => name.trim() && onConfirm(name.trim(), folderId || undefined)}
            disabled={!name.trim()}
            style={{ ...BTN(true), opacity: name.trim() ? 1 : 0.5, cursor: name.trim() ? 'pointer' : 'not-allowed' }}
          >
            Create
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── New Folder Modal ─────────────────────────────────────────────────────────

function NewFolderModal({ parentId, onConfirm, onClose }: {
  parentId?: string;
  onConfirm: (name: string) => void;
  onClose: () => void;
}) {
  const [name, setName] = useState('');
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.7)' }}>
      <div style={{ background: '#18181b', border: '1px solid #3f3f46', borderRadius: 10, padding: 24, width: 320, display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div style={{ fontSize: 15, fontWeight: 600, color: '#e4e4e7' }}>{parentId ? 'New Subfolder' : 'New Folder'}</div>
        <input
          autoFocus value={name} onChange={e => setName(e.target.value)}
          placeholder="Folder name"
          style={INPUT}
          onKeyDown={e => { if (e.key === 'Enter' && name.trim()) onConfirm(name.trim()); }}
        />
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={BTN()}>Cancel</button>
          <button onClick={() => name.trim() && onConfirm(name.trim())} disabled={!name.trim()}
            style={{ ...BTN(true), opacity: name.trim() ? 1 : 0.5 }}>
            Create
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Detail Panel ─────────────────────────────────────────────────────────────

function DetailPanel({ fn, folders, onEditInRecorder, onDelete, onDuplicate }: {
  fn: DBFunction;
  folders: DBFunctionFolder[];
  onEditInRecorder: (fn: DBFunction) => void;
  onDelete: (fn: DBFunction) => void;
  onDuplicate: (fn: DBFunction) => void;
}) {
  const [editingName, setEditingName] = useState(false);
  const [nameVal, setNameVal] = useState(fn.name);
  const [editingDesc, setEditingDesc] = useState(false);
  const [descVal, setDescVal] = useState(fn.description ?? '');
  const [showAddParam, setShowAddParam] = useState(false);
  const [paramName, setParamName] = useState('');
  const [paramDesc, setParamDesc] = useState('');
  const [paramDefault, setParamDefault] = useState('');

  // Keep local state in sync when fn changes (different function selected)
  useMemo(() => {
    setNameVal(fn.name);
    setDescVal(fn.description ?? '');
    setEditingName(false);
    setEditingDesc(false);
    setShowAddParam(false);
  }, [fn.id]);  // eslint-disable-line react-hooks/exhaustive-deps

  const steps: IRStep[] = useMemo(() => {
    try { return JSON.parse(fn.irJson) as IRStep[]; } catch { return []; }
  }, [fn.irJson]);

  const currentFolder = folders.find(f => f.id === fn.folderId);

  const saveName = async () => {
    const trimmed = nameVal.trim();
    if (!trimmed || trimmed === fn.name) { setEditingName(false); setNameVal(fn.name); return; }
    await db.functions.update(fn.id, { name: trimmed, updatedAt: Date.now() });
    setEditingName(false);
  };

  const saveDesc = async () => {
    const trimmed = descVal.trim();
    await db.functions.update(fn.id, { description: trimmed || undefined, updatedAt: Date.now() });
    setEditingDesc(false);
  };

  const changeFolder = async (folderId: string) => {
    await db.functions.update(fn.id, { folderId: folderId || undefined, updatedAt: Date.now() });
  };

  const addParam = async () => {
    if (!paramName.trim()) return;
    const newParam: DBFunctionParam = {
      name: paramName.trim(),
      description: paramDesc.trim() || undefined,
      defaultValue: paramDefault.trim() || undefined,
    };
    const updated = [...fn.parameters, newParam];
    await db.functions.update(fn.id, { parameters: updated, updatedAt: Date.now() });
    setParamName(''); setParamDesc(''); setParamDefault('');
    setShowAddParam(false);
  };

  const removeParam = async (idx: number) => {
    const updated = fn.parameters.filter((_, i) => i !== idx);
    await db.functions.update(fn.id, { parameters: updated, updatedAt: Date.now() });
  };

  const downloadFn = () => {
    const blob = new Blob([JSON.stringify({ ...fn }, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = `${fn.name}.plex-fn.json`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {/* Function header */}
      <div style={{ padding: '16px 20px', borderBottom: '1px solid #27272a', display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0 }}>
        <span style={{ fontSize: 18, color: '#a78bfa' }}>⚙</span>

        {/* Editable name */}
        {editingName ? (
          <input
            autoFocus value={nameVal} onChange={e => setNameVal(e.target.value)}
            onBlur={saveName}
            onKeyDown={e => { if (e.key === 'Enter') void saveName(); if (e.key === 'Escape') { setEditingName(false); setNameVal(fn.name); } }}
            style={{ ...INPUT, fontSize: 16, fontWeight: 700, width: 220, padding: '3px 8px' }}
          />
        ) : (
          <span
            style={{ fontSize: 16, fontWeight: 700, color: '#e4e4e7', cursor: 'text' }}
            onDoubleClick={() => { setNameVal(fn.name); setEditingName(true); }}
            title="Double-click to rename"
          >
            {fn.name}
          </span>
        )}

        {/* Actions */}
        <div style={{ display: 'flex', gap: 8, marginLeft: 'auto', alignItems: 'center' }}>
          <button onClick={() => onEditInRecorder(fn)} style={{ ...BTN(true), gap: 6 }}>
            ✎ Edit Steps in Recorder
          </button>
          <button onClick={downloadFn} style={BTN()}>↓ Download</button>
          <button onClick={() => onDuplicate(fn)} style={BTN()}>□ Duplicate</button>
          <button onClick={() => onDelete(fn)} style={BTN(false, true)}>✕ Delete</button>
        </div>
      </div>

      {/* Scrollable body */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '20px' }}>

        {/* Folder row */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
          <span style={{ fontSize: 12, color: '#71717a' }}>Folder:</span>
          <select
            value={fn.folderId ?? ''}
            onChange={e => void changeFolder(e.target.value)}
            style={{ ...INPUT, width: 'auto', minWidth: 140, padding: '4px 8px', fontSize: 12 }}
          >
            <option value="">— No folder —</option>
            {folders.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
          </select>
          {currentFolder && (
            <span style={{ fontSize: 11, color: '#52525b' }}>({currentFolder.name})</span>
          )}
        </div>

        {/* Description */}
        <div style={{ marginBottom: 16 }}>
          {editingDesc ? (
            <input
              autoFocus value={descVal} onChange={e => setDescVal(e.target.value)}
              onBlur={() => void saveDesc()}
              onKeyDown={e => { if (e.key === 'Enter') void saveDesc(); if (e.key === 'Escape') { setEditingDesc(false); setDescVal(fn.description ?? ''); } }}
              placeholder="Add a description..."
              style={{ ...INPUT, fontSize: 13 }}
            />
          ) : (
            <div
              onClick={() => { setDescVal(fn.description ?? ''); setEditingDesc(true); }}
              style={{
                fontSize: 13, color: fn.description ? '#d4d4d8' : '#52525b',
                cursor: 'text', padding: '2px 0',
                fontStyle: fn.description ? 'normal' : 'italic',
              }}
            >
              {fn.description || 'Click to add description...'}
            </div>
          )}
        </div>

        {/* Badges */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 24 }}>
          <span style={BADGE}>{fn.stepCount} step{fn.stepCount !== 1 ? 's' : ''}</span>
          <span style={BADGE}>{fn.parameters.length} parameter{fn.parameters.length !== 1 ? 's' : ''}</span>
        </div>

        {/* Parameters section */}
        <section style={{ marginBottom: 28 }}>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
            <span style={{ fontSize: 14, fontWeight: 600, color: '#e4e4e7', flex: 1 }}>Parameters</span>
            {!showAddParam && (
              <button onClick={() => setShowAddParam(true)} style={{ ...BTN(), fontSize: 11 }}>+ Add</button>
            )}
          </div>
          <p style={{ fontSize: 12, color: '#71717a', margin: '0 0 10px' }}>
            Inputs for callers. Inside steps use <code style={{ color: '#a78bfa', background: '#1c1c1f', padding: '1px 5px', borderRadius: 3 }}>{'${paramName}'}</code>.
          </p>

          {fn.parameters.length === 0 && !showAddParam && (
            <p style={{ fontSize: 12, color: '#52525b', fontStyle: 'italic', margin: 0 }}>
              No parameters — this function uses no inputs.
            </p>
          )}

          {fn.parameters.map((p, i) => (
            <div key={i} style={{
              display: 'flex', alignItems: 'center', gap: 10, padding: '7px 10px',
              background: '#18181b', borderRadius: 6, marginBottom: 6, border: '1px solid #27272a',
            }}>
              <code style={{ fontSize: 12, color: '#a78bfa', fontWeight: 600, minWidth: 90 }}>{p.name}</code>
              {p.description && <span style={{ fontSize: 12, color: '#71717a', flex: 1 }}>{p.description}</span>}
              {p.defaultValue && (
                <span style={{ fontSize: 11, color: '#52525b' }}>default: <code style={{ color: '#d4d4d8' }}>{p.defaultValue}</code></span>
              )}
              <button onClick={() => void removeParam(i)}
                style={{ background: 'none', border: 'none', color: '#52525b', cursor: 'pointer', padding: '2px 4px', fontSize: 12, marginLeft: 'auto' }}>
                ✕
              </button>
            </div>
          ))}

          {showAddParam && (
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '10px', background: '#18181b', borderRadius: 6, border: '1px solid #27272a' }}>
              <input
                autoFocus placeholder="name *" value={paramName} onChange={e => setParamName(e.target.value)}
                style={{ ...INPUT, width: 120 }}
                onKeyDown={e => { if (e.key === 'Enter') void addParam(); if (e.key === 'Escape') setShowAddParam(false); }}
              />
              <input
                placeholder="description" value={paramDesc} onChange={e => setParamDesc(e.target.value)}
                style={{ ...INPUT, width: 160 }}
              />
              <input
                placeholder="default value" value={paramDefault} onChange={e => setParamDefault(e.target.value)}
                style={{ ...INPUT, width: 120 }}
              />
              <button onClick={() => void addParam()} style={{ ...BTN(true), flexShrink: 0 }}>Add</button>
              <button onClick={() => { setShowAddParam(false); setParamName(''); setParamDesc(''); setParamDefault(''); }}
                style={{ ...BTN(), flexShrink: 0 }}>Cancel</button>
            </div>
          )}
        </section>

        {/* Steps section */}
        <section style={{ marginBottom: 28 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: '#e4e4e7', marginBottom: 10 }}>
            Steps ({steps.length})
          </div>
          {steps.length === 0 ? (
            <div style={{ fontSize: 12, color: '#52525b', fontStyle: 'italic' }}>
              No steps yet — click "Edit Steps in Recorder" to add steps.
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {steps.map((step, i) => (
                <div key={i} style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  padding: '6px 10px', background: '#18181b', borderRadius: 5,
                  border: '1px solid #1c1c1f',
                }}>
                  <span style={{ fontSize: 10, color: '#52525b', width: 18, textAlign: 'right', flexShrink: 0 }}>{i + 1}</span>
                  <span style={STEP_TYPE_BADGE}>{step.type}</span>
                  <span style={{ fontSize: 12, color: '#a1a1aa', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {stepDisplayLabel(step)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* How to call */}
        <section>
          <div style={{ fontSize: 14, fontWeight: 600, color: '#e4e4e7', marginBottom: 8 }}>
            How to call this function
          </div>
          <p style={{ fontSize: 12, color: '#71717a', margin: '0 0 10px' }}>
            In the Command Toolbar → Control category → ⚙ Call Flow
          </p>
          <div style={{
            background: '#18181b', border: '1px solid #27272a', borderRadius: 6,
            padding: '12px 16px', fontFamily: 'monospace', fontSize: 12, color: '#c4b5fd',
          }}>
            Call: {fn.name}
          </div>
        </section>
      </div>
    </div>
  );
}

// ─── Empty state ──────────────────────────────────────────────────────────────

function EmptyDetail({ onNewFunction }: { onNewFunction: () => void }) {
  return (
    <div style={{
      flex: 1, display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center', gap: 16, color: '#52525b',
    }}>
      <span style={{ fontSize: 40 }}>⚙</span>
      <div style={{ textAlign: 'center' }}>
        <div style={{ fontSize: 15, fontWeight: 600, color: '#71717a', marginBottom: 6 }}>Function Library</div>
        <div style={{ fontSize: 12 }}>Select a function from the sidebar, or create a new one.</div>
      </div>
      <button onClick={onNewFunction} style={{ ...BTN(true), fontSize: 13, padding: '8px 18px' }}>
        + New Function
      </button>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export function FunctionsPage({ onEditInRecorder }: {
  onEditInRecorder: (fn: DBFunction) => void;
}) {
  const allFolders  = useLiveQuery(() => db.functionFolders.orderBy('createdAt').toArray()) ?? [];
  const allFunctions = useLiveQuery(() => db.functions.orderBy('createdAt').toArray()) ?? [];

  const [selectedFnId, setSelectedFnId]     = useState<string | null>(null);
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(new Set());
  const [search, setSearch]                 = useState('');
  const [showNewFnModal, setShowNewFnModal] = useState(false);
  const [newFolderParent, setNewFolderParent] = useState<string | undefined>(undefined);
  const [showNewFolderModal, setShowNewFolderModal] = useState(false);

  // Build folder tree
  const tree = useMemo(() => buildFolderTree(allFolders, allFunctions), [allFolders, allFunctions]);

  // Unfoldered functions
  const rootFns = useMemo(
    () => allFunctions.filter(fn => !fn.folderId).sort((a, b) => a.name.localeCompare(b.name)),
    [allFunctions],
  );

  // Search-filtered functions
  const filtered = useMemo(() => {
    if (!search.trim()) return null;
    const q = search.toLowerCase();
    return allFunctions.filter(fn => fn.name.toLowerCase().includes(q));
  }, [search, allFunctions]);

  const selectedFn = allFunctions.find(fn => fn.id === selectedFnId) ?? null;

  const toggleFolder = (folderId: string) => {
    setExpandedFolders(prev => {
      const next = new Set(prev);
      if (next.has(folderId)) next.delete(folderId); else next.add(folderId);
      return next;
    });
  };

  // ── CRUD operations ───────────────────────────────────────────────────────

  const handleCreateFunction = async (name: string, folderId?: string) => {
    const fn: DBFunction = {
      id: newId(), name, folderId,
      parameters: [], irJson: '[]', stepCount: 0,
      createdAt: Date.now(), updatedAt: Date.now(),
    };
    await db.functions.add(fn);
    setSelectedFnId(fn.id);
    setShowNewFnModal(false);
    if (folderId) setExpandedFolders(prev => new Set([...prev, folderId]));
  };

  const handleCreateFolder = async (name: string) => {
    const folder: DBFunctionFolder = {
      id: newId(), name, parentId: newFolderParent, createdAt: Date.now(),
    };
    await db.functionFolders.add(folder);
    setShowNewFolderModal(false);
    setExpandedFolders(prev => new Set([...prev, folder.id]));
  };

  const handleDeleteFunction = async (fn: DBFunction) => {
    if (!confirm(`Delete function "${fn.name}"? This cannot be undone.`)) return;
    await db.functions.delete(fn.id);
    if (selectedFnId === fn.id) setSelectedFnId(null);
  };

  const handleDuplicateFunction = async (fn: DBFunction) => {
    const copy: DBFunction = {
      ...fn, id: newId(), name: `${fn.name} copy`,
      createdAt: Date.now(), updatedAt: Date.now(),
    };
    await db.functions.add(copy);
    setSelectedFnId(copy.id);
  };

  const handleDeleteFolder = async (folder: DBFunctionFolder) => {
    const fnsInFolder = allFunctions.filter(fn => fn.folderId === folder.id);
    if (fnsInFolder.length > 0) {
      if (!confirm(`Delete folder "${folder.name}" and its ${fnsInFolder.length} function(s)? This cannot be undone.`)) return;
      for (const fn of fnsInFolder) { await db.functions.delete(fn.id); if (selectedFnId === fn.id) setSelectedFnId(null); }
    }
    await db.functionFolders.delete(folder.id);
  };

  const handleExportFolder = (folder: DBFunctionFolder) => {
    const fns = allFunctions.filter(fn => fn.folderId === folder.id);
    const blob = new Blob([JSON.stringify({ folder, functions: fns }, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = `${folder.name}.plex-folder.json`; a.click();
    URL.revokeObjectURL(url);
  };

  const handleExportAll = () => {
    const blob = new Blob([JSON.stringify({ folders: allFolders, functions: allFunctions }, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = 'plex-functions.json'; a.click();
    URL.revokeObjectURL(url);
  };

  const handleImport = () => {
    const input = document.createElement('input');
    input.type = 'file'; input.accept = '.json';
    input.onchange = async () => {
      const file = input.files?.[0]; if (!file) return;
      try {
        const text = await file.text();
        const data = JSON.parse(text) as { folders?: DBFunctionFolder[]; functions?: DBFunction[] };
        const now = Date.now();
        if (data.folders) { for (const f of data.folders) { await db.functionFolders.put({ ...f, id: newId(), createdAt: now }); } }
        if (data.functions) { for (const fn of data.functions) { await db.functions.put({ ...fn, id: newId(), createdAt: now, updatedAt: now }); } }
      } catch { alert('Invalid file format'); }
    };
    input.click();
  };

  const handleAddSubfolder = (parentId: string) => {
    setNewFolderParent(parentId);
    setShowNewFolderModal(true);
  };

  return (
    <div style={PAGE}>
      {/* Modals */}
      {showNewFnModal && (
        <NewFunctionModal folders={allFolders} onConfirm={(n, f) => void handleCreateFunction(n, f)} onClose={() => setShowNewFnModal(false)} />
      )}
      {showNewFolderModal && (
        <NewFolderModal parentId={newFolderParent} onConfirm={n => void handleCreateFolder(n)} onClose={() => setShowNewFolderModal(false)} />
      )}

      {/* Left sidebar */}
      <div style={SIDEBAR}>
        {/* Sidebar header */}
        <div style={{ padding: '14px 14px 10px', borderBottom: '1px solid #27272a', flexShrink: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: '#e4e4e7' }}>Function Library</div>
          <div style={{ fontSize: 10, color: '#52525b', marginTop: 3 }}>
            {allFunctions.length} function{allFunctions.length !== 1 ? 's' : ''}
          </div>
        </div>

        <SidebarSearch value={search} onChange={setSearch} />

        {/* Tree or search results */}
        <div style={{ flex: 1, overflowY: 'auto', paddingTop: 4 }}>
          {filtered ? (
            // Search mode: flat filtered list
            filtered.length === 0 ? (
              <div style={{ padding: '20px 14px', fontSize: 12, color: '#52525b', textAlign: 'center' }}>
                No functions match "{search}"
              </div>
            ) : (
              filtered.map(fn => (
                <FnRow key={fn.id} fn={fn} depth={0} selected={selectedFnId === fn.id} onSelect={() => setSelectedFnId(fn.id)} />
              ))
            )
          ) : (
            <>
              {/* Root-level (no folder) functions */}
              {rootFns.map(fn => (
                <FnRow key={fn.id} fn={fn} depth={0} selected={selectedFnId === fn.id} onSelect={() => setSelectedFnId(fn.id)} />
              ))}
              {/* Folder tree */}
              {tree.map(node => (
                <FolderRow
                  key={node.folder.id}
                  node={node}
                  depth={0}
                  selectedFnId={selectedFnId}
                  expanded={expandedFolders.has(node.folder.id)}
                  onToggle={() => toggleFolder(node.folder.id)}
                  onSelectFn={fn => setSelectedFnId(fn.id)}
                  onAddSubfolder={handleAddSubfolder}
                  onDeleteFolder={f => void handleDeleteFolder(f)}
                  onExportFolder={handleExportFolder}
                />
              ))}
              {rootFns.length === 0 && tree.length === 0 && (
                <div style={{ padding: '20px 14px', fontSize: 12, color: '#52525b', textAlign: 'center' }}>
                  No functions yet
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Right side */}
      <div style={DETAIL}>
        {/* Page header bar */}
        <div style={{
          padding: '10px 20px', borderBottom: '1px solid #27272a',
          display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0,
          background: '#09090b',
        }}>
          <div>
            <span style={{ fontSize: 16, fontWeight: 700, color: '#e4e4e7' }}>Function Library</span>
            <span style={{ fontSize: 11, color: '#52525b', marginLeft: 10 }}>
              Reusable step sequences — call them from any test via ⚙ Call Flow.
            </span>
          </div>
          <div style={{ display: 'flex', gap: 8, marginLeft: 'auto' }}>
            <button onClick={() => { setNewFolderParent(undefined); setShowNewFolderModal(true); }} style={BTN()}>
              + Folder
            </button>
            <button onClick={handleImport} style={BTN()}>↑ Import</button>
            <button onClick={handleExportAll} style={BTN()}>
              ↓ Export All ({allFunctions.length})
            </button>
            <button onClick={() => setShowNewFnModal(true)} style={{ ...BTN(true), fontWeight: 600 }}>
              + New Function
            </button>
          </div>
        </div>

        {/* Detail area */}
        {selectedFn ? (
          <DetailPanel
            key={selectedFn.id}
            fn={selectedFn}
            folders={allFolders}
            onEditInRecorder={onEditInRecorder}
            onDelete={fn => void handleDeleteFunction(fn)}
            onDuplicate={fn => void handleDuplicateFunction(fn)}
          />
        ) : (
          <EmptyDetail onNewFunction={() => setShowNewFnModal(true)} />
        )}
      </div>
    </div>
  );
}
